import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ExternalLink, FileText, GripVertical, Instagram, ImagePlus, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import ShareToInstagram from "@/components/ShareToInstagram";

const blankSection = () => ({ num: "", category: "", title: "", body: "", cue: "" });

const blankForm = () => ({
  id: null,
  slug: "",
  title: "",
  excerpt: "",
  author: "Coach Chris Persaud",
  date: new Date().toISOString().slice(0, 10),
  read_time: "5 min read",
  level: "All Levels",
  category: "Training",
  cover: "",
  keywords: "", // comma separated
  intro: "", // blank-line separated paragraphs
  sections: [blankSection()],
  outro_title: "Ready to Take It Further?",
  outro_body: "If you want structured coaching that meets you where you are — at home, or in the gym — let's connect.",
  published: true,
});

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function BlogPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm());
  const slugTouchedRef = useRef(false);
  const coverInputRef = useRef(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);

  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd);
      setForm((f) => ({ ...f, cover: data.url }));
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingCover(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/blog/posts");
      setPosts(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    slugTouchedRef.current = false;
    setForm(blankForm());
    setOpen(true);
  };

  const openEdit = (p) => {
    slugTouchedRef.current = true;
    setForm({
      id: p.id,
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt,
      author: p.author,
      date: p.date,
      read_time: p.read_time,
      level: p.level,
      category: p.category,
      cover: p.cover,
      keywords: (p.keywords || []).join(", "),
      intro: (p.intro || []).join("\n\n"),
      sections: (p.sections && p.sections.length ? p.sections : [blankSection()]).map((s) => ({
        num: s.num || "",
        category: s.category || "",
        title: s.title || "",
        body: s.body || "",
        cue: s.cue || "",
      })),
      outro_title: p.outro?.title || "",
      outro_body: p.outro?.body || "",
      status: p.status || (p.published ? "published" : "draft"),
    });
    setOpen(true);
  };

  const onTitleChange = (v) => {
    setForm((f) => ({ ...f, title: v, slug: slugTouchedRef.current ? f.slug : slugify(v) }));
  };

  const updateSection = (i, patch) => {
    setForm((f) => {
      const next = f.sections.slice();
      next[i] = { ...next[i], ...patch };
      return { ...f, sections: next };
    });
  };
  const addSection = () => {
    setForm((f) => {
      const nextNum = String(f.sections.length + 1).padStart(2, "0");
      return { ...f, sections: [...f.sections, { ...blankSection(), num: nextNum }] };
    });
  };
  const removeSection = (i) => {
    setForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));
  };
  const moveSection = (i, dir) => {
    setForm((f) => {
      const arr = f.sections.slice();
      const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, sections: arr };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.slug.trim()) return toast.error("Slug is required");
    if (!form.cover.trim()) return toast.error("Cover image is required");
    if (!form.excerpt.trim()) return toast.error("Excerpt is required");

    const payload = {
      slug: slugify(form.slug),
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      author: form.author.trim() || "Coach Chris Persaud",
      date: form.date,
      read_time: form.read_time.trim() || "5 min read",
      level: form.level.trim() || "All Levels",
      category: form.category.trim() || "Training",
      cover: form.cover.trim(),
      keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
      intro: form.intro.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
      sections: form.sections
        .filter((s) => s.title.trim() && s.body.trim())
        .map((s, idx) => ({
          num: s.num.trim() || String(idx + 1).padStart(2, "0"),
          category: s.category.trim(),
          title: s.title.trim(),
          body: s.body.trim(),
          cue: s.cue.trim(),
        })),
      outro: { title: form.outro_title.trim(), body: form.outro_body.trim() },
      status: form.status,
    };

    setSaving(true);
    try {
      if (form.id) {
        await api.patch(`/admin/blog/posts/${form.id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog/posts", payload);
        toast.success("Post created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const cycleStatus = async (p) => {
    const current = p.status || (p.published ? "published" : "draft");
    const next = current === "published" ? "draft" : current === "draft" ? "coming_soon" : "published";
    try {
      await api.patch(`/admin/blog/posts/${p.id}`, { status: next });
      toast.success(`Set to ${next.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/posts/${p.id}`);
      toast.success("Post deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="blog-posts-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Content · SEO</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Blog Posts.</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">Publish drills, technique breakdowns, and coaching insights. Posts go live at <span className="text-zinc-300">/blog/&lt;slug&gt;</span> and are indexed for SEO.</p>
        </div>
        <Button onClick={openNew} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="new-post-btn">
          <Plus className="h-4 w-4 mr-2" /> New post
        </Button>
      </div>

      <div className="border border-[#27272A] bg-[#0F0F0F]">
        <table className="w-full text-sm" data-testid="posts-table">
          <thead className="border-b border-[#27272A] text-zinc-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="text-left p-4">Title</th>
              <th className="text-left p-4">Category</th>
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Status</th>
              <th className="p-4 w-40 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-12 text-center text-zinc-500">Loading…</td></tr>
            ) : posts.length === 0 ? (
              <tr><td colSpan={5} className="p-12 text-center text-zinc-500">
                <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
                No posts yet. Click &ldquo;New post&rdquo; to create your first one.
              </td></tr>
            ) : posts.map((p) => (
              <tr key={p.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`post-row-${p.slug}`}>
                <td className="p-4">
                  <div className="font-display uppercase tracking-tight text-white">{p.title}</div>
                  <div className="text-[11px] text-zinc-500 mt-1">/{p.slug}</div>
                </td>
                <td className="p-4 text-zinc-400 text-xs uppercase tracking-widest">{p.category}</td>
                <td className="p-4 text-zinc-500 text-xs">{p.date}</td>
                <td className="p-4">
                  {(() => {
                    const s = p.status || (p.published ? "published" : "draft");
                    const cfg =
                      s === "published"
                        ? { label: "Published", cls: "border-emerald-700 bg-emerald-950/40 text-emerald-300" }
                        : s === "coming_soon"
                        ? { label: "Coming Soon", cls: "border-amber-700 bg-amber-950/40 text-amber-300" }
                        : { label: "Draft", cls: "border-zinc-700 bg-zinc-900 text-zinc-400" };
                    return (
                      <button
                        onClick={() => cycleStatus(p)}
                        data-testid={`cycle-status-${p.slug}`}
                        className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${cfg.cls}`}
                        title="Click to cycle: Published → Draft → Coming Soon → Published"
                      >
                        {cfg.label}
                      </button>
                    );
                  })()}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    {p.published && (
                      <button
                        onClick={() => setShareTarget(p)}
                        className="p-2 text-zinc-500 hover:text-[#FF331F]"
                        data-testid={`share-post-${p.slug}`}
                        title="Share to Instagram"
                      >
                        <Instagram className="h-4 w-4" />
                      </button>
                    )}
                    {p.published && (
                      <Link to={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-zinc-500 hover:text-white" data-testid={`view-post-${p.slug}`} title="View live">
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    )}
                    <button onClick={() => openEdit(p)} className="p-2 text-zinc-500 hover:text-white" data-testid={`edit-post-${p.slug}`} title="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(p)} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`delete-post-${p.slug}`} title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="post-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">{form.id ? "Edit Post" : "New Post"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
              {form.id ? "Update content and save changes." : "Fill in the details below to publish a new article."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-5">
            {/* Title + slug */}
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Title *</Label>
                <Input required value={form.title} onChange={(e) => onTitleChange(e.target.value)} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Slug (URL) *</Label>
                <Input
                  required
                  value={form.slug}
                  onChange={(e) => { slugTouchedRef.current = true; setForm({ ...form, slug: e.target.value }); }}
                  onBlur={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  className="bg-[#050505] border-[#27272A] rounded-sm font-mono lowercase"
                  data-testid="post-slug-input"
                />
                <p className="text-[11px] text-zinc-500">/blog/{form.slug || "your-post-slug"}</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Excerpt *</Label>
              <Textarea required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-excerpt-input" />
              <p className="text-[11px] text-zinc-500">Shown in card previews and meta descriptions.</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Cover image URL *</Label>
              <Input required type="url" value={form.cover} onChange={(e) => setForm({ ...form, cover: e.target.value })} placeholder="https://…" className="bg-[#050505] border-[#27272A] rounded-sm font-mono text-xs" data-testid="post-cover-input" />
              {form.cover && (
                <img src={form.cover} alt="cover preview" className="mt-2 max-h-40 border border-[#27272A] object-cover" />
              )}
            </div>

            <div className="grid md:grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-category-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Level</Label>
                <Input value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-level-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Read time</Label>
                <Input value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} placeholder="5 min read" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-readtime-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Date</Label>
                <Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-date-input" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Author</Label>
              <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-author-input" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">SEO Keywords (comma-separated)</Label>
              <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="boxing drills, footwork, shadow boxing" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-keywords-input" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Intro paragraphs</Label>
              <Textarea value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} rows={5} placeholder="First paragraph of your intro.&#10;&#10;Second paragraph (separate with a blank line)." className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-intro-input" />
              <p className="text-[11px] text-zinc-500">Separate paragraphs with a blank line.</p>
            </div>

            {/* Sections */}
            <div className="space-y-3 pt-2 border-t border-[#27272A]">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest text-zinc-300 font-bold">Sections ({form.sections.length})</Label>
                <Button type="button" onClick={addSection} variant="outline" size="sm" className="border-[#27272A] bg-[#050505] text-white hover:bg-[#121212] text-xs uppercase tracking-widest rounded-sm" data-testid="add-section-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add section
                </Button>
              </div>

              {form.sections.map((s, i) => (
                <div key={i} className="border border-[#27272A] bg-[#050505] p-4 space-y-3" data-testid={`section-block-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest">
                      <GripVertical className="h-4 w-4" /> Section {i + 1}
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => moveSection(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-white disabled:opacity-30 text-xs px-2" title="Move up" data-testid={`move-up-${i}`}>↑</button>
                      <button type="button" onClick={() => moveSection(i, 1)} disabled={i === form.sections.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-30 text-xs px-2" title="Move down" data-testid={`move-down-${i}`}>↓</button>
                      {form.sections.length > 1 && (
                        <button type="button" onClick={() => removeSection(i)} className="text-zinc-500 hover:text-red-400" title="Remove" data-testid={`remove-section-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Num</Label>
                      <Input value={s.num} onChange={(e) => updateSection(i, { num: e.target.value })} placeholder="01" className="bg-[#0A0A0A] border-[#27272A] rounded-sm" data-testid={`section-num-${i}`} />
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Eyebrow / Category</Label>
                      <Input value={s.category} onChange={(e) => updateSection(i, { category: e.target.value })} placeholder="Footwork" className="bg-[#0A0A0A] border-[#27272A] rounded-sm" data-testid={`section-category-${i}`} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Title</Label>
                    <Input value={s.title} onChange={(e) => updateSection(i, { title: e.target.value })} className="bg-[#0A0A0A] border-[#27272A] rounded-sm" data-testid={`section-title-${i}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Body</Label>
                    <Textarea value={s.body} onChange={(e) => updateSection(i, { body: e.target.value })} rows={4} className="bg-[#0A0A0A] border-[#27272A] rounded-sm" data-testid={`section-body-${i}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Coach&apos;s Cue (optional)</Label>
                    <Textarea value={s.cue} onChange={(e) => updateSection(i, { cue: e.target.value })} rows={2} className="bg-[#0A0A0A] border-[#27272A] rounded-sm" data-testid={`section-cue-${i}`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Outro / CTA */}
            <div className="space-y-3 pt-2 border-t border-[#27272A]">
              <Label className="text-xs uppercase tracking-widest text-zinc-300 font-bold">Closing CTA</Label>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Title</Label>
                <Input value={form.outro_title} onChange={(e) => setForm({ ...form, outro_title: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-outro-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Body</Label>
                <Textarea value={form.outro_body} onChange={(e) => setForm({ ...form, outro_body: e.target.value })} rows={3} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="post-outro-body-input" />
              </div>
            </div>

            {/* Publish status */}
            <div className="pt-3 border-t border-[#27272A] space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-300 font-bold">Status</Label>
              <div className="flex flex-wrap gap-2" data-testid="post-status-picker">
                {[
                  { v: "published", label: "Published", desc: "Live on /blog & full body indexable.", cls: "border-emerald-700 hover:bg-emerald-950/30 text-emerald-300", on: "bg-emerald-950/50 border-emerald-500 text-emerald-200" },
                  { v: "coming_soon", label: "Coming Soon", desc: "Shows a locked teaser card. Body hidden.", cls: "border-amber-700 hover:bg-amber-950/30 text-amber-300", on: "bg-amber-950/50 border-amber-500 text-amber-200" },
                  { v: "draft", label: "Draft", desc: "Admin-only. Not on public list.", cls: "border-zinc-700 hover:bg-zinc-800 text-zinc-300", on: "bg-zinc-800 border-zinc-500 text-white" },
                ].map((opt) => {
                  const active = form.status === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setForm({ ...form, status: opt.v })}
                      className={`text-left p-3 border rounded-sm flex-1 min-w-[160px] transition-colors ${active ? opt.on : `bg-[#050505] ${opt.cls}`}`}
                      data-testid={`status-pick-${opt.v}`}
                    >
                      <div className="text-xs uppercase tracking-widest font-bold mb-1">{opt.label}</div>
                      <div className="text-[11px] text-zinc-500">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <DialogFooter className="pt-4 border-t border-[#27272A]">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-[#27272A] bg-[#050505] text-white hover:bg-[#121212] rounded-sm">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] font-bold uppercase tracking-widest rounded-sm" data-testid="save-post-btn">
                {saving ? "Saving…" : (form.id ? "Save changes" : "Publish post")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ShareToInstagram open={!!shareTarget} onOpenChange={(o) => !o && setShareTarget(null)} post={shareTarget} />
    </div>
  );
}
